import type { DomainManifest } from 'modules/core/lib/manifests/types'
import type { Navigation, NavigationPaths } from 'modules/core/lib/navigation/context'

import { ArchiveSurveyActivity } from 'modules/engagement_surveys/activities/ArchiveSurvey'
import { CreateSurveyActivity } from 'modules/engagement_surveys/activities/CreateSurvey'
import { DeleteSurveyActivity } from 'modules/engagement_surveys/activities/DeleteSurvey'
import { DuplicateSurveyActivity } from 'modules/engagement_surveys/activities/DuplicateSurvey'
import { FinishSurveyActivity } from 'modules/engagement_surveys/activities/FinishSurvey'
import { PublishSurveyActivity } from 'modules/engagement_surveys/activities/PublishSurvey'
import { RemindRespondentsActivity } from 'modules/engagement_surveys/activities/RemindRespondents'

export const manifest = {
  sidebar: (_navigation: Navigation<NavigationPaths>) => ({
    items: [],
  }),
  activities: {
    duplicateSurvey: {
      component: DuplicateSurveyActivity,
      guidelines:
        'Opens the duplicate engagement survey overlay to create a copy of an existing survey. Use when the user wants to duplicate or copy an existing engagement survey.',
      params: {
        surveyId: {
          guidelines:
            'The ID of the engagement survey to duplicate. First, check if the current URL matches engagement/smart_surveys/{id}/* and extract the survey ID from it. If not on a survey page, use the fetchSurveys tool to list available surveys and resolve the correct ID. Never ask the user for the survey ID or URL.',
        },
      },
    },
    deleteSurvey: {
      component: DeleteSurveyActivity,
      guidelines:
        'Opens the delete engagement survey overlay to confirm deleting a survey. Use when the user wants to delete an engagement survey.',
      params: {
        surveyId: {
          guidelines:
            'The ID of the engagement survey to delete. First, check if the current URL matches engagement/smart_surveys/{id}/* and extract the survey ID from it. If not on a survey page, use the fetchSurveys tool to list available surveys and resolve the correct ID. Never ask the user for the survey ID or URL.',
        },
      },
    },
    createSurvey: {
      component: CreateSurveyActivity,
      guidelines:
        'Opens the create engagement survey overlay to start a new survey. Use when the user wants to create a new engagement survey.',
    },
    archiveSurvey: {
      component: ArchiveSurveyActivity,
      guidelines:
        'Opens the archive engagement survey overlay to archive a survey. Use when the user wants to archive an engagement survey.',
      params: {
        surveyId: {
          guidelines:
            'The ID of the engagement survey to archive. First, check if the current URL matches engagement/smart_surveys/{id}/* and extract the survey ID from it. If not on a survey page, use the fetchSurveys tool to list available surveys and resolve the correct ID. Never ask the user for the survey ID or URL.',
        },
      },
    },
    finishSurvey: {
      component: FinishSurveyActivity,
      guidelines:
        'Opens the finish survey overlay to mark a survey as finished. Use when the user wants to finish or close an engagement survey.',
      params: {
        formId: {
          guidelines:
            "The ID of the form associated with the engagement survey to finish. First, check if the current URL matches engagement/smart_surveys/{id}/* and extract the survey ID from it. Then use the fetchSurveys tool to find that survey and read its formId. If not on a survey page, use the fetchSurveys tool to list available surveys and resolve the correct formId based on the user's request. Never ask the user for internal IDs or URLs.",
        },
      },
    },
    publishSurvey: {
      component: PublishSurveyActivity,
      guidelines:
        'Opens the publish engagement survey overlay to publish a survey. Use when the user wants to publish an engagement survey.',
      params: {
        surveyId: {
          guidelines:
            'The ID of the engagement survey to publish. First, check if the current URL matches engagement/smart_surveys/{id}/* and extract the survey ID from it. If not on a survey page, use the fetchSurveys tool to list available surveys and resolve the correct ID. Never ask the user for the survey ID or URL.',
        },
      },
      availableForOne: true,
    },
    remindRespondents: {
      component: RemindRespondentsActivity,
      guidelines:
        'Opens the remind respondents dialog to send reminder notifications to respondents who have not yet completed an engagement survey. Use when the user wants to remind or nudge survey respondents.',
      params: {
        surveyId: {
          guidelines:
            'The ID of the engagement survey whose respondents should be reminded. First, check if the current URL matches engagement/smart_surveys/{id}/* and extract the survey ID from it. If not on a survey page, use the fetchSurveys tool to list available surveys and resolve the correct ID. Never ask the user for the survey ID or URL.',
        },
        formId: {
          guidelines: 'The ID of the form associated with the survey.',
        },
      },
    },
  },
} as const satisfies DomainManifest
